import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import { toast } from "sonner";
import {
  SYSTEM_ROLE_OPTIONS,
  SYSTEM_ROLE_LABELS,
  SPECIALIZATION_OPTIONS,
  SPECIALIZATION_LABELS,
  type SystemRole,
} from "@/lib/constants";
import { useCreateEmployee } from "@/hooks/use-create-employee";

/**
 * Knapp + dialog for å opprette ny ansatt. Erstatter den gamle
 * `NyAnsattCard`-komponenten som lå i Innstillinger (VIB-706).
 * Plasseres i headeren på /ansatte.
 */
export function NyAnsattDialog() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const create = useCreateEmployee();
  const emailInputRef = useRef<HTMLInputElement>(null);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<SystemRole>("user");
  const [specialization, setSpecialization] = useState<string>("");
  const [jobTitle, setJobTitle] = useState("");
  const [phone, setPhone] = useState("");
  const [hireDate, setHireDate] = useState("");
  const [monthlyCapacityHours, setMonthlyCapacityHours] = useState("141.25");

  const reset = () => {
    setFullName("");
    setEmail("");
    setRole("user");
    setSpecialization("");
    setJobTitle("");
    setPhone("");
    setHireDate("");
    setMonthlyCapacityHours("141.25");
  };

  const handleSubmit = async () => {
    if (!fullName.trim() || !email.trim()) {
      toast.error("Fullt navn og e-post er påkrevd");
      return;
    }

    if (emailInputRef.current && !emailInputRef.current.validity.valid) {
      toast.error("Ugyldig e-postadresse");
      return;
    }

    const parsedCapacity = monthlyCapacityHours
      ? Number(monthlyCapacityHours)
      : null;

    if (parsedCapacity !== null && !Number.isFinite(parsedCapacity)) {
      toast.error("Ugyldig månedskapasitet");
      return;
    }

    try {
      const created = await create.mutateAsync({
        full_name: fullName.trim(),
        email: email.trim(),
        role,
        specialization: specialization || null,
        job_title: jobTitle.trim() || null,
        phone: phone.trim() || null,
        hire_date: hireDate || null,
        monthly_capacity_hours: parsedCapacity ?? 141.25,
      });
      toast.success(`Ansatt "${fullName}" opprettet`);
      setOpen(false);
      reset();
      if (created?.id) {
        navigate(`/ansatte/${created.id}`);
      }
    } catch (err) {
      toast.error(`Kunne ikke opprette ansatt: ${(err as Error).message}`);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="default" size="sm">
          <SrIcon name="plus" className="w-3 h-3 mr-1" />
          Ny ansatt
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Ny ansatt</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label htmlFor="ny-ansatt-navn">Fullt navn *</Label>
            <Input
              id="ny-ansatt-navn"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="F.eks. Ola Nordmann"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ny-ansatt-epost">E-post *</Label>
            <Input
              ref={emailInputRef}
              id="ny-ansatt-epost"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ola@solvrev.no"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Rolle *</Label>
              <Select value={role} onValueChange={(v) => setRole(v as SystemRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SYSTEM_ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {SYSTEM_ROLE_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Spesialisering</Label>
              <Select
                value={specialization || "_none"}
                onValueChange={(v) =>
                  setSpecialization(v === "_none" ? "" : v)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Velg" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Ingen</SelectItem>
                  {SPECIALIZATION_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {SPECIALIZATION_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ny-ansatt-tittel">Jobbtittel</Label>
              <Input
                id="ny-ansatt-tittel"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ny-ansatt-tlf">Telefon</Label>
              <Input
                id="ny-ansatt-tlf"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ny-ansatt-startdato">Startdato</Label>
              <Input
                id="ny-ansatt-startdato"
                type="date"
                value={hireDate}
                onChange={(e) => setHireDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ny-ansatt-kap">Kapasitet (t/mnd)</Label>
              <Input
                id="ny-ansatt-kap"
                type="number"
                min={0}
                step={0.25}
                value={monthlyCapacityHours}
                onChange={(e) => setMonthlyCapacityHours(e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setOpen(false);
              reset();
            }}
            disabled={create.isPending}
          >
            Avbryt
          </Button>
          <Button onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending && (
              <SrIcon name="loader2" className="w-3 h-3 mr-1 animate-spin" />
            )}
            Opprett
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
